<template>
  <v-card class="pa-4">
    <h2>Ratio Calculator by Closed Files (Insurer)</h2>
    <v-row dense>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedClassification"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="File Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="selectedInsurers"
          :items="insurerOptions"
          item-text="name"
          item-value="id"
          label="Insurer"
          dense
          outlined
          required
          multiple
          :menu-props="{ maxHeight: '400px' }"
          :chips="true"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-menu v-model="menuMonth" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedMonth"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            @change="menuMonth = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="
            !selectedDept ||
            !selectedClassification ||
            !selectedInsurers.length ||
            !selectedMonth
          "
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <v-alert v-if="alertMessage" type="warning" outlined>
      {{ alertMessage }}
    </v-alert>
    <v-alert v-else-if="noDataMessage" type="warning" outlined>
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined>
      Select department, file classification, insurer, and month, then click
      "Generate Report" to view data.
    </v-alert>
    <div v-if="reportData.length">
      <v-simple-table>
        <thead>
          <tr>
            <th>Insurer</th>
            <th>Within TAT</th>
            <th>TAT Breach</th>
            <th>Total</th>
            <th>Breach %</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in reportData">
            <tr :key="row.insurerCode">
              <td>
                <strong>{{ row.insurerCode }}</strong>
              </td>
              <td>{{ row.withinTat }}</td>
              <td class="red--text font-weight-bold">{{ row.breachedTat }}</td>
              <td>{{ row.total }}</td>
              <td>
                <span :style="breachCellStyle(row.percent)" class="px-2 py-1">
                  {{ row.percent }}
                </span>
              </td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.insurerCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.insurerCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.withinTat }}</td>
              <td>{{ detail.breachedTat }}</td>
              <td>{{ detail.total }}</td>
              <td>
                <span
                  :style="breachCellStyle(detailPercent(detail))"
                  class="px-2 py-1"
                >
                  {{ detailPercent(detail) }}
                </span>
              </td>
            </tr>
          </template>
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.withinTat }}</strong>
            </td>
            <td class="red--text font-weight-bold">
              <strong>{{ totals.breachedTat }}</strong>
            </td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
            <td>
              <strong>{{ totals.breachPercent }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
  </v-card>
</template>

<script>
import moment from "moment";
import InsDataService from "../../services/InsDataService";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "RatioCalculatorInsurer",
  data() {
    return {
      deptOptions: [],
      selectedDept: "all",
      insurerOptions: [],
      selectedInsurers: [], // changed from selectedInsurer
      classOptions: [],
      filteredClassOptions: [],
      selectedClassification: "all",
      selectedMonth: null,
      menuMonth: false,
      alertMessage: "",
      reportData: [],
      totals: {
        withinTat: 0,
        breachedTat: 0,
        total: 0,
        breachPercent: "0.00",
      },
      noDataMessage: "",
    };
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClassification = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
  },
  methods: {
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [
        { id: "all", name: "ALL DEPARTMENTS" },
        ...res.data,
      ].sort((a, b) => a.name.localeCompare(b.name));
    },
    async loadInsurers() {
      try {
        const res = await InsDataService.getAll();
        this.insurerOptions = res.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch {
        this.insurerOptions = [];
      }
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ].sort((a, b) => {
        if (a.id === "all") return -1;
        if (b.id === "all") return 1;
        return a.subCode.localeCompare(b.subCode);
      });
    },
    filterClassifications(deptId) {
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        withinTat: 0,
        breachedTat: 0,
        total: 0,
        breachPercent: "0.00",
      };
      this.noDataMessage = "";
      this.alertMessage = "";
      try {
        const params = {
          deptGroup: this.selectedDept,
          classificationId: this.selectedClassification,
          insurerIds: this.selectedInsurers.join(","),
          month: moment(this.selectedMonth).format("YYYY-MM"),
        };
        const query = Object.entries(params)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join("&");
        const res = await fetch(
          `${apiConfig.baseURL}/compliance/ratio-calculator-insurer?${query}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = await res.json();
        this.processReportData(data);
        if (!Array.isArray(data.reportData) || data.reportData.length === 0) {
          const monthStr = moment(this.selectedMonth).format("MMMM YYYY");
          this.noDataMessage = `No ratio data found for ${monthStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      let withinTat = 0,
        breachedTat = 0,
        total = 0;
      this.reportData.forEach((row) => {
        withinTat += row.withinTat;
        breachedTat += row.breachedTat;
        total += row.total;
      });
      this.totals.withinTat = withinTat;
      this.totals.breachedTat = breachedTat;
      this.totals.total = total;
      this.totals.breachPercent =
        total > 0 ? ((breachedTat / total) * 100).toFixed(2) : "0.00";
    },
    breachCellStyle(percent) {
      const val = parseFloat(percent);
      if (val > 80) {
        return "background:#FF6600;color:white;border-radius:4px;";
      } else if (val >= 50) {
        return "background:#FFB366;color:black;border-radius:4px;";
      }
      return "";
    },
    detailPercent(detail) {
      return detail.total > 0
        ? ((detail.breachedTat / detail.total) * 100).toFixed(2)
        : "0.00";
    },
    async exportExcel() {
      this.alertMessage = "";
      if (!this.reportData.length) return;
      const params = {
        deptGroup: this.selectedDept,
        classificationId: this.selectedClassification,
        insurerIds: this.selectedInsurers.join(","),
        month: moment(this.selectedMonth).format("YYYY-MM"),
      };
      const query = Object.entries(params)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");
      const url = `${apiConfig.baseURL}/compliance/ratio-calculator-insurer/export?${query}`;
      try {
        const res = await fetch(url, { method: "GET" });
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await res.json();
          if (data.alert) {
            this.alertMessage = data.alert;
            return;
          }
        }
        window.open(url, "_blank");
      } catch (err) {
        this.alertMessage =
          err.message || "Failed to export Excel. Please try again.";
      }
    },
  },
  mounted() {
    this.loadDepartments();
    this.loadInsurers();
    this.loadClassifications();
  },
};
</script>

<style scoped></style>
